// Domain-owned route containers. Do not import legacy connected-container or all-domain registries here.
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import type { EvidenceItem, WorkflowCommand } from "@swot/swot-components";
import { readCmtsSession } from "../../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../../session/cmtsSessionResolver.js";
import { useCmtsDispatch } from "@store/hooks.js";
import { authActions } from "@store/authSlice.js";
import { permissionActions } from "@store/permissionSlice.js";
import { tenantActions } from "@store/tenantSlice.js";
import { serializeReduxError } from "@store/thunkExtra.js";
import { useCmtsAuth } from "../../auth/authHooks.js";
import { useComplaintRegister } from "@hooks/useComplaintRegister.js";
import { useComplaintDetail } from "@hooks/useComplaintDetail.js";
import { useComplaintWorkflow } from "@hooks/useComplaintWorkflow.js";
import { useEvidence } from "@hooks/useEvidence.js";
import { cmtsKeys } from "@hooks/cmtsQueryKeys.js";
import type { RepositoryResult } from "../../repositories/repositoryResult.js";
import type { CmtsHostRuntime } from "../../../../app/appRuntime.js";
import type { CmtsComplaintDetailVm, CmtsComplaintRecordVm } from "../../view-models/complaintViewModels.js";
import type { CmtsConfigurationDraftVm, CmtsConfigurationVersionVm } from "../../mappers/configurationMappers.js";
import { queryObjectFromSearch } from "../../filters/cmtsFilterEngine.js";
import { decideCmtsExport, createCmtsExportAuditPayload } from "../../export/cmtsSecureExport.js";
import { getCmtsPersonaLandingPath } from "../../routes/cmtsPersonaLanding.js";
import { normalizeTypedReportCode, reportCodeFromPathname } from "../../reporting/cmtsReportConfig.js";
import { throwCmtsCanonicalError } from "../../api/cmtsApiErrors.js";
import { cmtsMasterDataEntityForPathSegment } from "../../master-data/cmtsEnterpriseMasterDataRegistry.js";
import { activeRows, assertRepositoryOk, businessValueOptionOf, complaintQuery, dataOr, diagnosticsOf, emptyBackendDashboard, errorOf, invalidateAdminArea, loadMasterOptions, optionOf, tenantId, requireBackendRowId, requireLinkedComplaintId, useAdminDependencyData, useCmtsRouterSearch, type CmtsConnectedContainerProps } from "../domainContainerSupport.js";
import { CmtsRcaPage } from "@modules/pages/cmtsRcaPage.js";
import { CmtsCapaPage } from "@modules/pages/cmtsCapaPage.js";
import { CmtsVerificationPage } from "@modules/pages/cmtsVerificationPage.js";
import { requireCmtsGuid } from "../../utils/cmtsGuid.js";

function useComplaintId(): string | undefined {
  return useParams<{ complaintId: string }>().complaintId;
}

function useConnectedComplaint(runtime: CmtsHostRuntime) {
  const id = useComplaintId();
  const t = tenantId(runtime);
  const detail = useComplaintDetail(runtime.repositories.complaints, id, t);
  const workflow = useComplaintWorkflow(runtime.repositories.workflow, id, t);
  const executeCommand = async (command: WorkflowCommand) => {
    await workflow.execute.mutateAsync(command);
    const reloadComplaintDetail = detail.refetch; await reloadComplaintDetail();
  };
  const record = dataOr<CmtsComplaintDetailVm | null>(detail.data, null);
  const allowed = dataOr(workflow.allowedActions.data, []);
  const mergedRecord = record ? { ...record, allowedActions: allowed } : record;
  return { id, tenantId: t, detail, workflow, record: mergedRecord, diagnostics: diagnosticsOf(runtime.diagnostics, detail.data, workflow.allowedActions.data), executeCommand };
}

export function CmtsCapaContainer({ runtime }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const connected = useConnectedComplaint(runtime);
  const queryClient = useQueryClient();
  const location = useLocation();
  const searchText = useCmtsRouterSearch();
  const { recordId } = useParams();
  const hasComplaintContext = Boolean(connected.id);
  const key = hasComplaintContext ? cmtsKeys.capa(connected.id ?? "", connected.tenantId) : ["cmts", "capa-queue", connected.tenantId, searchText];
  const isCapaRoute = location.pathname === "/cmts/capa" || location.pathname.startsWith("/cmts/rca-capa/capa");
  const query = useQuery({ queryKey: key, queryFn: () => hasComplaintContext ? runtime.repositories.capa.list(connected.id ?? "", connected.tenantId) : runtime.repositories.capa.queue(connected.tenantId, queryObjectFromSearch(searchText)), enabled: hasComplaintContext || isCapaRoute });
  const actionTypeOptions = useQuery({ queryKey: ["cmts", "master-options", "capa-action-types", connected.tenantId], queryFn: () => loadMasterOptions(runtime, "capa-action-types", connected.tenantId, "CAPA action type"), retry: 1 });
 const refreshCapaScopes = async () => {
  await queryClient.invalidateQueries({
    queryKey: key,
  });

  await queryClient.invalidateQueries({
    predicate: (queryToCheck) =>
      Array.isArray(
        queryToCheck.queryKey,
      ) &&
      queryToCheck.queryKey[0] ===
        "cmts" &&
      queryToCheck.queryKey[1] ===
        "capa-queue",
  });

  await Promise.all([
    query.refetch(),
    connected.detail.refetch(),
  ]);
};
 const create = useMutation({
  mutationFn: (
    payload: Record<string, unknown>,
  ) => {
    const complaintId =
      requireCmtsGuid(
        payload.complaintId ??
          connected.id,
        "CAPA create complaintId",
      );

    return runtime.repositories.capa.create(
      complaintId,
      payload,
      connected.tenantId,
    );
  },

  onSuccess: refreshCapaScopes,
});
  const complete = useMutation({ mutationFn: (row: Record<string, unknown>) => {
    const complaintId = requireCmtsGuid(row.complaintId ?? connected.id, "CAPA complete complaintId");
    const capaId = requireBackendRowId(row, ["capaId", "id"], "CAPA complete");
    return runtime.repositories.capa.complete(complaintId, capaId, { action: row.action ?? "complete", remarks: row.remarks ?? row.reason ?? "CAPA action executed from canonical CMTS form", reason: row.reason }, connected.tenantId);
  }, onSuccess: refreshCapaScopes, });
return (
  <CmtsCapaPage {...(recordId ? { selectedRecordId: recordId } : {})} />
);

}

export function CmtsRcaContainer({ runtime }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const connected = useConnectedComplaint(runtime);
  const queryClient = useQueryClient();
  const location = useLocation();
  const searchText = useCmtsRouterSearch();
  const { recordId } = useParams();
  const hasComplaintContext = Boolean(connected.id);
  const key = hasComplaintContext ? cmtsKeys.rca(connected.id ?? "", connected.tenantId) : ["cmts", "rca-queue", connected.tenantId, searchText];
  const isRcaRoute = location.pathname === "/cmts/rca" || location.pathname.startsWith("/cmts/rca-capa/rca") || location.pathname === "/cmts/rca-capa";
  const query = useQuery({ queryKey: key, queryFn: () => hasComplaintContext ? runtime.repositories.rca.list(connected.id ?? "", connected.tenantId) : runtime.repositories.rca.queue(connected.tenantId, queryObjectFromSearch(searchText)), enabled: hasComplaintContext || isRcaRoute });
  const methodOptions = useQuery({ queryKey: ["cmts", "master-options", "rca-method", connected.tenantId], queryFn: () => loadMasterOptions(runtime, "rca-method", connected.tenantId, "RCA method"), retry: 1 });
  const invalidateRcaScopes = async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    await queryClient.invalidateQueries({ predicate: (queryToCheck) => Array.isArray(queryToCheck.queryKey) && queryToCheck.queryKey[0] === "cmts" && queryToCheck.queryKey[1] === "rca-queue" });
    await connected.detail.refetch();
  };
   const refreshRcaScopes =
  async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: key,
      }),

      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(
            query.queryKey,
          ) &&
          query.queryKey[0] ===
            "cmts" &&
          query.queryKey[1] ===
            "rca-queue",
      }),
    ]);

    await Promise.all([
      query.refetch(),
      connected.detail.refetch(),
    ]);
  };
  const create = useMutation({ mutationFn: (payload: Record<string, unknown>) => {
    const complaintId = requireCmtsGuid(payload.complaintId ?? connected.id, "RCA create complaintId");
    return runtime.repositories.rca.create(complaintId, payload, connected.tenantId);
  }, onSuccess: invalidateRcaScopes });
  const complete = useMutation({ mutationFn: (row: Record<string, unknown>) => {
    const complaintId = requireCmtsGuid(row.complaintId ?? connected.id, "RCA complete complaintId");
    const rcaId = requireBackendRowId(row, ["rcaId", "id"], "RCA complete");
    return runtime.repositories.rca.complete(complaintId, rcaId, { action: row.action ?? "complete", remarks: row.remarks ?? row.reason ?? "RCA action executed from canonical CMTS form", reason: row.reason }, connected.tenantId);
  }, onSuccess: invalidateRcaScopes });
  return <CmtsRcaPage {...(recordId ? { selectedRecordId: recordId } : {})} />;
}

export function CmtsVerificationContainer({ runtime }: CmtsConnectedContainerProps) {
  const navigate = useNavigate();
  const connected = useConnectedComplaint(runtime);
  const queryClient = useQueryClient();
  const location = useLocation();
  const searchText = useCmtsRouterSearch();
  const { recordId } = useParams();
  const hasComplaintContext = Boolean(connected.id);
  const key = ["cmts", "verification", hasComplaintContext ? connected.id : "queue", connected.tenantId, searchText];
  const isVerificationRoute = location.pathname === "/cmts/verification" || location.pathname.startsWith("/cmts/rca-capa/verification");
  const query = useQuery({ queryKey: key, queryFn: () => hasComplaintContext ? runtime.repositories.verification.queue(connected.tenantId, { complaintId: connected.id }) : runtime.repositories.verification.queue(connected.tenantId, queryObjectFromSearch(searchText)), enabled: hasComplaintContext || isVerificationRoute });
  const verificationMethodOptions = useQuery({ queryKey: ["cmts", "master-options", "verification-methods", connected.tenantId], queryFn: () => loadMasterOptions(runtime, "verification-methods", connected.tenantId, "Verification method"), retry: 1 });
  const verificationDecisionOptions = useQuery({ queryKey: ["cmts", "master-options", "verification-decision", connected.tenantId], queryFn: () => loadMasterOptions(runtime, "verification-decision", connected.tenantId, "Verification decision"), retry: 1 });
  const prepare = useMutation({ mutationFn: (payload: Record<string, unknown>) => {
    const complaintId = requireCmtsGuid(payload.complaintId ?? connected.id, "Verification create complaintId");
    return runtime.repositories.verification.prepare(complaintId, payload, connected.tenantId);
  }, onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: key }); await connected.detail.refetch(); } });
  const approve = useMutation({ mutationFn: (row: Record<string, unknown>) => runtime.repositories.verification.approve(String(row.complaintId ?? connected.id ?? ""), String(row.verificationId ?? row.id ?? ""), { remarks: "Approved from canonical CMTS form" }, connected.tenantId), onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: key }); await connected.detail.refetch(); } });
  const reject = useMutation({ mutationFn: (row: Record<string, unknown>) => runtime.repositories.verification.reject(String(row.complaintId ?? connected.id ?? ""), String(row.verificationId ?? row.id ?? ""), { remarks: "Rejected from canonical CMTS form" }, connected.tenantId), onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: key }); await connected.detail.refetch(); } });
  return <CmtsVerificationPage {...(recordId ? { selectedRecordId: recordId } : {})} />;
}
